import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "@me8eon/translation";
import { useJwtToken } from "@me8eon/react_authentication";
import useOnboardingPortalStore from "../OnboardingPortal.Store";
import { EonUiTooltip, EonUiPagination } from "@eon-ui/eon-ui-components-react";
import AppOnboardingPreviewCard from "../AppOnboardingPreviewCard";
import { useLanguage } from "@me8eon/language";
import OnboardingPortalAppIcon from "../components/OnboardingPortalAppIcon";
import OnboardingPortalTableHeaders from "../components/OnboardingPortalTableHeaders";
import OnboardingPortalStatusFilter from "../components/OnboardingPortalStatusFilter";
import OnboardingPortalSearchFilter from "../components/OnboardingPortalSearchFilter";
import OnboardingPortalExportButton from "../components/OnboardingPortalExportButton";
import OnboardingPortalLoader from "../components/OnboardingPortalLoader";
import OnboardingPortalRefreshButton from "../components/OnboardingPortalRefreshButton";
import {
    exportAppsDataAsCsv,
    exportAppsDataAsExcel,
} from "../OnboardingPortal.export.utils";

const ManageAppsScreen = () => {
    const t = useTranslation();
    const jwt = useJwtToken();
    const {
        showModule,
        onboardedApps,
        fetchOnboardedApps,
        onEditApp,
        onViewApp,
        isLoadingOnboardedApps,
    } = useOnboardingPortalStore();
    const [lang] = useLanguage();
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("all");
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const filteredApps = useMemo(
        () =>
            onboardedApps.filter((app) => {
                const matchesSearch =
                    app.title.toLowerCase().includes(search.toLowerCase()) ||
                    app.subTitle.toLowerCase().includes(search.toLowerCase());
                const matchesStatus =
                    status === "all" ? true : app.status === status;
                return matchesSearch && matchesStatus;
            }),
        [search, status, onboardedApps]
    );

    useEffect(() => {
        const cp = currentPage;
        const ps = pageSize;
        setCurrentPage(1);
        if (cp > 1 && Math.ceil(filteredApps.length / ps) < cp) {
            setCurrentPage(1);
        }
    }, [filteredApps]);

    useEffect(() => {
        if (showModule === "manage-apps") {
            fetchOnboardedApps(jwt);
        }
    }, [showModule]);
    return (
        <>
            <div className="onboarding-portal--content--card">
                {isLoadingOnboardedApps && <OnboardingPortalLoader />}
                <div className="onboarding-portal--content--card--header">
                    <div className="onboarding-portal--content--card--header--text">
                        {t("onboardingPortal.tableHeaders.apps")} (
                        {filteredApps.length})
                    </div>

                    <div className="onboarding-portal--content--card--header--actions">
                        <OnboardingPortalRefreshButton
                            onClick={() => fetchOnboardedApps(jwt)}
                        />
                        <OnboardingPortalSearchFilter
                            t={t}
                            search={search}
                            setSearch={setSearch}
                        />
                        <OnboardingPortalStatusFilter
                            t={t}
                            status={status}
                            setStatus={setStatus}
                        />
                        <OnboardingPortalExportButton
                            t={t}
                            onExportCSV={() =>
                                exportAppsDataAsCsv(filteredApps, "apps_export")
                            }
                            onExportExcel={() =>
                                exportAppsDataAsExcel(
                                    filteredApps,
                                    "apps_export"
                                )
                            }
                        />
                    </div>
                </div>
                <table className="onboarding-portal--table">
                    <OnboardingPortalTableHeaders
                        headers={[
                            "onboardingPortal.tableHeaders.icon",
                            "onboardingPortal.tableHeaders.title",
                            "onboardingPortal.tableHeaders.subTitle",
                            "onboardingPortal.tableHeaders.status",
                            "onboardingPortal.tableHeaders.comments",
                            "onboardingPortal.tableHeaders.action",
                        ]}
                        t={t}
                    />
                    <tbody>
                        {filteredApps
                            .slice(
                                (currentPage - 1) * pageSize,
                                currentPage * pageSize
                            )
                            .map((app) => (
                                <tr key={app.id}>
                                    <td>
                                        <OnboardingPortalAppIcon
                                            icon={app.icon}
                                            iconUrl={app.iconDataUrl}
                                        />
                                    </td>
                                    <td>{app.title}</td>
                                    <td>{app.subTitle}</td>
                                    <td>
                                        <span
                                            className={`status--${app.status}`}
                                        >
                                            {t(
                                                `onboardingPortal.statusLabel.${app.status}`
                                            )}
                                        </span>
                                    </td>
                                    <td>
                                        {app.status !== "pending"
                                            ? app.reviewerNote
                                            : "--"}
                                    </td>
                                    <td>
                                        <div className="actions-row">
                                            <div>
                                                <span className="action--preview">
                                                    Preview
                                                </span>
                                                <EonUiTooltip
                                                    visibility="onHover"
                                                    placement="top"
                                                >
                                                    <div className="onboarding-portal--form--preview">
                                                        <AppOnboardingPreviewCard
                                                            data={{
                                                                title: app.title,
                                                                subTitle:
                                                                    app.subTitle,
                                                                iconUrl:
                                                                    app.iconDataUrl,
                                                                icon: app.icon,
                                                            }}
                                                        />
                                                    </div>
                                                </EonUiTooltip>
                                            </div>
                                            <span
                                                className="action--view"
                                                onClick={() => onViewApp(app)}
                                            >
                                                {t("onboardingPortal.viewBtn")}
                                            </span>
                                            <span
                                                className="action--edit"
                                                onClick={() => onEditApp(app)}
                                            >
                                                {t("onboardingPortal.editBtn")}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            <EonUiPagination
                lang={lang}
                begin={
                    filteredApps.length === 0 ? 0 : (currentPage - 1) * pageSize
                }
                currentPage={currentPage}
                end={Math.min(currentPage * pageSize, filteredApps.length)}
                initialPageSize={pageSize}
                pageSize={pageSize}
                pageSizes={"5,10,20,50,100"}
                totalItems={filteredApps.length}
                onPageChanged={(e) => {
                    setCurrentPage(e.detail as any);
                    console.log(e.detail);
                }}
                onPageSizeChanged={(e) => {
                    setPageSize(e.detail);
                    setCurrentPage(1);
                }}
                onPageDecreased={(e) => {
                    console.log(e);
                    setCurrentPage((prev) => Math.max(prev - 1, 1));
                }}
                onPageIncreased={(e) => {
                    console.log(e);
                    setCurrentPage((prev) =>
                        Math.min(
                            prev + 1,
                            Math.ceil(filteredApps.length / pageSize)
                        )
                    );
                }}
            />
        </>
    );
};

export default ManageAppsScreen;
